import { useState } from 'react'
import { CloudOff } from 'lucide-react'
import { useColors } from '../hooks/useColors'

/**
 * Shown when the admin API cannot be reached.
 *
 * Reads as a backend problem, because that is what it is from the dashboard's
 * point of view — it received a 503 and has nothing to render. It states that
 * and stops: no status code, no route, no stack, no hint about cause or
 * duration. Anything more specific would either be a guess or an information
 * leak.
 *
 * The retry genuinely re-attempts. It is not decoration: when the backend
 * recovers, this is how an operator gets back in without being told to reload.
 */
export default function ServiceUnavailablePage() {
  const colors = useColors()
  const [retrying, setRetrying] = useState(false)

  const retry = () => {
    setRetrying(true)
    // A full reload re-runs the boot probe and every provider from scratch,
    // which is the honest way to recover: any surviving in-memory state from
    // the failed session would be half-loaded anyway.
    window.location.reload()
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: colors.bgPrimary,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: '999px',
            background: 'rgba(85,107,82,0.10)',
            border: '1px solid rgba(85,107,82,0.22)',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 24px',
          }}
        >
          <CloudOff size={32} color="var(--color-muted)" aria-hidden="true" />
        </div>

        <h1
          className="font-display"
          style={{ fontSize: 24, fontWeight: 700, color: colors.textPrimary, margin: 0, lineHeight: 1.3 }}
        >
          Something went wrong
        </h1>

        <p
          className="font-body"
          style={{ fontSize: 15, color: colors.textSecondary, marginTop: 12, lineHeight: 1.6 }}
        >
          We couldn&apos;t reach the server. This is usually temporary — please try
          again in a few minutes.
        </p>

        <button
          type="button"
          onClick={retry}
          disabled={retrying}
          className="font-body"
          style={{
            marginTop: 28,
            height: 44,
            padding: '0 28px',
            borderRadius: 999,
            border: 'none',
            background: 'var(--color-gold)',
            color: 'var(--color-forest-dark)',
            fontSize: 14,
            fontWeight: 600,
            cursor: retrying ? 'default' : 'pointer',
            opacity: retrying ? 0.7 : 1,
          }}
        >
          {retrying ? 'Retrying…' : 'Try again'}
        </button>

        <p
          className="font-body"
          style={{ fontSize: 13, color: colors.textMuted, marginTop: 28 }}
        >
          Rapidry Admin
        </p>
      </div>
    </div>
  )
}
